// pdfViewSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_FILEURL } from '../components/constants';
import { authHeader } from '../utils/authHeaders';

export const SOURCE_FETCH_TIMEOUT_MS = 60000;
export const SOURCE_DOWNLOAD_TIMEOUT_MS = 300000;

export const fetchPDF = createAsyncThunk(
  'pdfViewer/fetchPDF',
  async ({ userId, taskId, filename, historyId, itemId }, { rejectWithValue }) => {
    const controller = new AbortController();
    let timer = setTimeout(() => controller.abort(), SOURCE_FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(`${BASE_FILEURL}/getfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        // sharepoint_item_id (when the clicked citation carried one) lets the
        // backend resolve the exact file even when two active files share this
        // filename — omitted (null) is a no-op, same as before this existed.
        body: JSON.stringify({ user_id: userId, task_id: taskId, filename, history_id: historyId, sharepoint_item_id: itemId || null }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      timer = setTimeout(() => controller.abort(), SOURCE_DOWNLOAD_TIMEOUT_MS);

      if (!response.ok) {
        let code = null;
        try {
          const body = await response.json();
          if (body?.detail && typeof body.detail === 'object') code = body.detail.code || null;
        } catch (e) {
          // non-JSON error body — fall through with no code
        }
        if (!code) code = response.status === 404 ? 'not_found' : 'load_failed';
        return rejectWithValue({ message: 'Failed to fetch PDF', code });
      }

      return await response.blob();
    } catch (err) {
      const code = err?.name === 'AbortError' ? 'timeout' : 'load_failed';
      return rejectWithValue({ message: err?.message || 'Failed to fetch PDF', code });
    } finally {
      clearTimeout(timer);
    }
  }
);

const pdfViewerSlice = createSlice({
  name: 'pdfViewer',
  initialState: {
    pdfUrl: null,
    loading: false,
    error: null,
  },
  reducers: {
    resetPdfUrl: (state) => {
      state.pdfUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPDF.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPDF.fulfilled, (state, action) => {
        state.loading = false;
        state.pdfUrl = action.payload;
      })
      .addCase(fetchPDF.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const { resetPdfUrl } = pdfViewerSlice.actions;
export default pdfViewerSlice.reducer;